import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LogOut, Menu, Moon, PenSquare, Sun, UserCircle2 } from 'lucide-react'
import { useAuth } from '@/providers/AuthProvider'
import { useTheme } from '@/providers/ThemeProvider'
import { useWorkspace } from '@/providers/WorkspaceProvider'
import { Button, useToast } from '@/components/ui'
import { cn } from '@/lib/cn'

/**
 * A barra superior não repete o título da tela: cada página é dona do seu
 * próprio `h1`. Aqui fica só o contexto (workspace ativo) e as ações.
 */
export function Topbar({ onOpenNav }: { onOpenNav: () => void }) {
  const { user, signOut } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const { workspace } = useWorkspace()
  const { toast } = useToast()
  const navigate = useNavigate()

  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    function onPointerDown(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false)
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  async function handleSignOut() {
    try {
      await signOut()
      navigate('/entrar', { replace: true })
    } catch (error) {
      toast.error('Não foi possível sair', {
        description: error instanceof Error ? error.message : undefined,
      })
    }
  }

  return (
    <header className="sticky top-0 z-20 border-b border-[var(--border-subtle)] bg-[var(--surface-card)]/85 backdrop-blur">
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center gap-3 px-4 sm:px-6 lg:px-8">
        {/* Único caminho para as 8 telas no mobile: 44×44, o alvo de toque cheio. */}
        <button
          type="button"
          onClick={onOpenNav}
          className="inline-flex size-11 items-center justify-center rounded-lg text-[var(--text-secondary)] hover:bg-[var(--surface-inset)] lg:hidden"
        >
          <Menu className="size-5" aria-hidden="true" />
          <span className="sr-only">Abrir menu de navegação</span>
        </button>

        <div className="min-w-0 flex-1">
          {workspace && (
            <p
              title={workspace.name}
              className="hidden truncate text-sm font-medium text-[var(--text-secondary)] sm:block"
            >
              <span className="sr-only">Workspace ativo: </span>
              {workspace.name}
            </p>
          )}
        </div>

        {/* Abaixo de `lg` a navegação lateral some: este é o único atalho de
            criação visível. No desktop o CTA vive no corpo da página. */}
        <Button
          size="icon"
          variant="secondary"
          aria-label="Criar publicação"
          onClick={() => navigate('/criar')}
          className="shrink-0 lg:hidden"
        >
          <PenSquare className="size-5" aria-hidden="true" />
        </Button>

        <button
          type="button"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
          className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-[var(--text-secondary)] transition hover:bg-[var(--surface-inset)] hover:text-[var(--text-primary)]"
        >
          {theme === 'dark' ? (
            <Sun className="size-5" aria-hidden="true" />
          ) : (
            <Moon className="size-5" aria-hidden="true" />
          )}
        </button>

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={user?.email ? `Menu da conta de ${user.email}` : 'Menu da conta'}
            className={cn(
              // `min-w-11` em vez de `size-11`: com o e-mail visível (md+) o
              // botão precisa crescer, mas nunca encolher abaixo de 44×44.
              'flex h-11 min-w-11 items-center justify-center gap-2 rounded-lg px-2 text-sm transition',
              'text-[var(--text-secondary)] hover:bg-[var(--surface-inset)] hover:text-[var(--text-primary)]',
            )}
          >
            <UserCircle2 className="size-6" aria-hidden="true" />
            <span className="hidden max-w-32 truncate md:inline">{user?.email ?? 'Conta'}</span>
          </button>

          {menuOpen && (
            <div
              role="menu"
              aria-label="Menu da conta"
              className="sh-card absolute right-0 top-full z-30 mt-2 w-56 overflow-hidden p-1"
              style={{ animation: 'sh-fade-in 120ms ease-out' }}
            >
              <div className="border-b border-[var(--border-subtle)] px-3 py-2">
                <p className="truncate text-sm font-medium text-[var(--text-primary)]">
                  {user?.email}
                </p>
                <p className="text-xs text-[var(--text-muted)]">Sessão ativa</p>
              </div>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false)
                  navigate('/ajustes')
                }}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-[var(--text-secondary)] hover:bg-[var(--surface-inset)]"
              >
                Ajustes
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={handleSignOut}
                className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-[var(--danger)] hover:bg-[var(--danger-soft)]"
              >
                <LogOut className="size-4" aria-hidden="true" />
                Sair da conta
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
